/**
 * VerificationShieldModal — Single source of truth for the shield icon + modal on web.
 * Computes its own title, color, and trust factors from raw post data.
 * Matches mobile VerificationShield exactly.
 */
import { X } from 'lucide-react'
import { useState } from 'react'

interface VerificationShieldModalProps {
  score?: number
  code?: string
  hasMedia?: boolean
  capturedAt?: string
  createdAt?: string
  size?: number
}

// ---------- Same logic as mobile VerificationShield ----------

function getShieldColor(score: number, hasMedia: boolean, code?: string): string {
  if (code && hasMedia) return '#008700'
  if (code && !hasMedia) return '#10B981'
  if (hasMedia) return '#9CA3AF'
  return '#10B981'
}

function getTitle(score: number, hasMedia: boolean, code?: string): string {
  if (code && hasMedia) return `Verified by OnRecord (${score}%)`
  if (code && !hasMedia) return 'Text Verified'
  if (hasMedia) return 'Unverified Origin'
  return 'Text Post'
}

function getTrustFactors(hasMedia: boolean, code?: string): string[] {
  if (code && hasMedia) {
    return [
      'Captured directly with OnRecord camera',
      'GPS location verified at capture time',
      'Device identity verified',
      'Content not tampered',
      'OnRecord tracking code assigned',
      'AI Analysis: Authentic content confirmed',
    ]
  }
  if (code && !hasMedia) {
    return [
      'Posted via OnRecord app',
      'Timestamp verified',
      'Account identity verified',
    ]
  }
  if (hasMedia) return ['Origin could not be verified']
  return []
}

function getMediaAgeInfo(capturedAt: string, createdAt: string) {
  const diffMs = new Date(createdAt).getTime() - new Date(capturedAt).getTime()
  if (diffMs < 60000) return { label: null, color: '#10B981' }
  const mins = Math.floor(diffMs / 60000)
  const hours = Math.floor(mins / 60)
  const days = Math.floor(hours / 24)
  const ageColor = days >= 1 ? '#DC2626' : '#F59E0B'
  if (mins < 60) return { label: `${mins}m old`, color: ageColor }
  if (hours < 24) return { label: `${hours}h old`, color: ageColor }
  return { label: `${days}d old`, color: ageColor }
}

// ---------- Component ----------

export default function VerificationShieldModal({
  score = 100,
  code,
  hasMedia = false,
  capturedAt,
  createdAt,
  size = 24,
}: VerificationShieldModalProps) {
  const [showModal, setShowModal] = useState(false)

  const color = getShieldColor(score, hasMedia, code)
  const title = getTitle(score, hasMedia, code)
  const trustFactors = getTrustFactors(hasMedia, code)

  const ageInfo = capturedAt && createdAt ? getMediaAgeInfo(capturedAt, createdAt) : null
  const mediaAge = ageInfo?.label || null
  const ageColor = ageInfo?.color || '#10B981'

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setShowModal(!showModal) }}
        className="rounded-full p-2 cursor-pointer flex items-center justify-center"
      >
        <div
          className="rounded-full flex items-center justify-center"
          style={{ backgroundColor: color, width: size, height: size }}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="white" stroke="none">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <path d="M9 12l2 2 4-4" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>

      {showModal && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowModal(false)} />
          <div
            className="absolute bottom-full left-0 mb-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 p-4 z-50"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: color }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white" stroke="none">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="flex-1">
                <div className="font-bold text-base text-gray-900">{title}</div>
                {code && <div className="text-xs font-mono text-gray-400">{code}</div>}
              </div>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Media Age */}
            {capturedAt && createdAt ? (
              mediaAge ? (
                <div
                  className="flex items-center gap-2 rounded-lg px-3 py-2.5 mb-4"
                  style={{ backgroundColor: ageColor === '#DC2626' ? '#FEE2E2' : '#FEF3C7' }}
                >
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke={ageColor} strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <span className="text-sm font-semibold" style={{ color: ageColor === '#DC2626' ? '#991B1B' : '#92400E' }}>
                    Media was {mediaAge} when posted
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2.5 mb-4">
                  <svg className="w-4 h-4 text-green-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                  </svg>
                  <span className="text-sm font-semibold text-green-800">Live capture — posted within seconds</span>
                </div>
              )
            ) : null}

            {/* Trust Factors */}
            {trustFactors.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Verified for</div>
                <div className="space-y-2">
                  {trustFactors.map((factor, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm">
                      <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="#10B981" stroke="none">
                        <circle cx="12" cy="12" r="12"/>
                        <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className="text-gray-700">{factor}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-center gap-1.5 pt-3 border-t border-gray-100">
              <svg className="w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
              <span className="text-[11px] text-gray-400">Verified by OnRecord Authenticity Engine</span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
